// V2 版式：waterfall（前端 DOM 版）——对照后端 _shared/pptx/v2/layouts/waterfall.ts 逐坐标移植
// 4–8 项步进柱：每柱悬浮在此前累计值上；delta=null 为合计柱（从 0 画到累计值）；
// 涨/跌/合计三色分立（semanticPair 查表）；相邻柱 dashed 台阶；每柱标 ± 数值（fontMono）。
// 几何取 V2GEO["waterfall"]（deno dump 同源，勿手改数值）。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import { semanticPair } from "./semantic";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

// deno dump 同源：与后端 waterfall.ts GEO 一一对应
const GEO = V2GEO["waterfall"] as any;

// waterfall block 结构以后端 _shared/pptx/types.ts 为准（slides.ts Block 已同步）
type WaterfallItem = { label: string; delta: number | null };
type WaterfallBlock = { type: "waterfall"; unit?: string; items: WaterfallItem[] };

export default function V2Waterfall({ c, skin, page, total, editable, handlers }: V2LayoutProps) {
  const m = SPACING.marginX;
  const W = 13.333;
  const sem = semanticPair(skin);

  const wi = c.blocks.findIndex((b) => (b as { type: string }).type === "waterfall");
  const wb = wi >= 0 ? (c.blocks[wi] as unknown as WaterfallBlock) : null;
  const items = wb ? wb.items.slice(0, 8) : [];
  const n = items.length;

  // 累计与值域（与后端同公式：yOf 值域映射、零线居中）
  const acc: number[] = [];
  let run = 0;
  items.forEach((it) => { run += it.delta ?? 0; acc.push(run); });
  const topOf = (i: number) => acc[i];
  const botOf = (i: number) => (items[i].delta === null ? 0 : acc[i] - (items[i].delta ?? 0));
  const allV = items.flatMap((_, i) => [topOf(i), botOf(i)]);
  const maxAbs = Math.max(...allV.map((v) => Math.abs(v)), 1e-9);

  const chartW = W - 2 * m;
  const colW = n > 0 ? chartW / n : chartW;
  const barW = colW * GEO.barWRatio;
  const plotH = GEO.chart.bottom - GEO.chart.top - GEO.labelH;
  const yOf = (v: number) => GEO.chart.top + GEO.labelH + ((maxAbs - v) / (2 * maxAbs)) * plotH;
  const y0 = GEO.chart.top + GEO.labelH + plotH / 2;

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "waterfall")} page={page} total={total} foot={footOf()} />

      {/* 页面标题（长度分档） */}
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(GEO.title.x), top: px(GEO.title.y),
          width: px(GEO.title.w), height: px(GEO.title.h),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: skin.serifHead ? 700 : 400, lineHeight: 1.15,
        }}
      />

      {/* 累计零线（hairline 细线，正负柱分界） */}
      <div style={{
        position: "absolute", left: px(m), top: px(y0),
        width: px(chartW), height: 1, background: skin.hairline,
      }} />

      {items.map((it, i) => {
        const cx = m + colW * i + (colW - barW) / 2;
        const isSub = it.delta === null;
        const delta = it.delta ?? 0;
        const top = acc[i];
        const bottom = isSub ? 0 : acc[i] - delta;
        const yTop = yOf(Math.max(top, bottom));
        const yBot = yOf(Math.min(top, bottom));
        const h = Math.max(yBot - yTop, 0.03);
        const fill = isSub ? skin.hairline : delta >= 0 ? sem.good : sem.bad;
        // 数值标签（± 符号 + 单位；合计柱标累计值——与后端同拼法）
        const sign = isSub ? "" : delta >= 0 ? "+" : "−";
        const valText = isSub
          ? acc[i].toLocaleString("en-US") + (wb?.unit ?? "")
          : `${sign}${Math.abs(delta).toLocaleString("en-US")}${wb?.unit ?? ""}`;
        const labelY = top >= bottom ? yTop - GEO.labelH : yBot + 0.02;
        return (
          <div key={i}>
            {/* 相邻柱 dashed 台阶连接（本柱顶 → 下一柱左端） */}
            {i < n - 1 && (
              <div style={{
                position: "absolute", left: px(cx + barW), top: px(yOf(top)),
                width: px(colW - barW), height: 0,
                borderTop: `1px dashed ${skin.sub}`, opacity: 0.75,
              }} />
            )}
            {/* 柱体（合计柱 hairline 中性 + text 细描边） */}
            <div style={{
              position: "absolute", left: px(cx), top: px(yTop),
              width: px(barW), height: px(h),
              background: fill,
              border: isSub ? `1px solid ${skin.text}` : undefined,
            }} />
            {/* 数值标签（fontMono） */}
            <EditableText
              value={valText} path={isSub ? `blocks.${wi}.items.${i}.label` : `blocks.${wi}.items.${i}.delta`}
              editable={editable && !isSub}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(cx - colW * 0.25), top: px(labelY),
                width: px(colW), height: px(GEO.labelH),
                fontSize: pt(skin.scale.body), color: isSub ? skin.text : delta >= 0 ? sem.good : sem.bad,
                fontFamily: skin.fontMono, fontWeight: isSub ? 700 : 400,
                textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center",
                whiteSpace: "nowrap",
              }}
            />
            {/* x 轴底标签 */}
            <EditableText
              value={it.label} path={`blocks.${wi}.items.${i}.label`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(cx - colW * 0.25), top: px(GEO.chart.bottom),
                width: px(colW), height: px(0.5),
                fontSize: pt(skin.scale.body), color: skin.sub, fontFamily: skin.fontBody,
                textAlign: "center", display: "flex", alignItems: "flex-start", justifyContent: "center",
              }}
            />
          </div>
        );
      })}
    </>
  );
}
